/**
 * Add-to-cart feedback — VNYL-1405.
 *
 * Both confirmation paths in the ticket share the same in-place button reporting
 * ("Adding…" -> "Added ✓") and header cart-icon pulse; they differ only in what confirms
 * the add:
 *   - browse-type pages (collection / shop / browse / search) — the drawer stays closed
 *     and the toast in this module confirms the add with a "View" CTA;
 *   - PDP / wishlist / everywhere else — the cart drawer opens once the add lands.
 *
 * Deliberately standalone: no jQuery, no React, no build step. all.js drives it from
 * handleBuyClick (see app.shouldToastAddToCart / app.showAddedToCartToast /
 * app.showAddedToCartDrawer) and it must be available on every page rendering a
 * `.button-buy`.
 */
(function () {
  'use strict';

  // VNYL-1405: "We show the toast for 3.5 seconds".
  var AUTO_DISMISS_MS = 3500;
  // How long the button holds "Added ✓" before reverting to its original label.
  var ADDED_LABEL_MS = 1800;
  // Safety net: a request that never resolves must not strand the button on "Adding…".
  var STUCK_RESET_MS = 8000;
  var PULSE_MS = 600;
  // Must match the transition duration in add-to-cart-feedback.css.
  var EXIT_MS = 260;
  // Cap on waiting for the product thumbnail before revealing the toast. Cart images are
  // usually already in cache from the grid, so this almost never bites; it exists so a
  // slow or dead CDN can't swallow the confirmation entirely.
  var PRELOAD_TIMEOUT_MS = 600;

  var ROOT_ID = 'vinyl-cart-toast';
  var CART_TOGGLE_ID = 'cart-toggle-button';
  var CART_MODAL_ID = 'myCartModal';

  var dismissTimer = null;
  var exitTimer = null;
  var pulseTimer = null;
  // Bumped on every showToast() so an image preload that resolves late — after a newer add
  // has already claimed the toast — can tell it is stale and bail out.
  var showToken = 0;
  // Per-button revert timers. WeakMap so React re-rendering a grid can't leak entries.
  var buttonTimers = new WeakMap();
  var bound = false;

  function root() {
    return document.getElementById(ROOT_ID);
  }

  /**
   * Ask the Shopify CDN for a thumbnail instead of the full-size cart image.
   * Cart line-item images already carry a `?v=` cache buster, hence the two branches.
   */
  function thumbUrl(url) {
    if (!url) return '';
    return url.indexOf('?') === -1 ? url + '?width=88' : url + '&width=88';
  }

  function clearDismissTimer() {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }

  function startDismissTimer() {
    clearDismissTimer();
    dismissTimer = setTimeout(hideToast, AUTO_DISMISS_MS);
  }

  /**
   * Open the cart drawer through the header toggle — the canonical open path, which also
   * drives the drawer's own click effect. Falls back to letting the anchor navigate to
   * /cart when the toggle isn't on the page.
   */
  function onCtaClick(event) {
    var toggle = document.getElementById(CART_TOGGLE_ID);
    if (!toggle) return;
    event.preventDefault();
    hideToast();
    toggle.click();
  }

  function bindOnce() {
    if (bound) return;
    var el = root();
    if (!el) return;
    bound = true;

    var cta = el.querySelector('.vinyl-cart-toast__cta');
    if (cta) cta.addEventListener('click', onCtaClick);

    // Don't yank the CTA out from under a user who is reaching for it.
    el.addEventListener('mouseenter', clearDismissTimer);
    el.addEventListener('focusin', clearDismissTimer);
    el.addEventListener('mouseleave', function () {
      if (el.classList.contains('is-visible')) startDismissTimer();
    });
    el.addEventListener('focusout', function () {
      if (el.classList.contains('is-visible')) startDismissTimer();
    });

    // The drawer is its own confirmation — a toast behind it is noise, and on mobile the
    // toast sits above the modal in the stacking order.
    var cartModal = document.getElementById(CART_MODAL_ID);
    if (cartModal) cartModal.addEventListener('show.bs.modal', hideToast);
  }

  /**
   * Fetch and decode the thumbnail off-document, so the toast is only ever revealed with
   * its image already paintable — no frame of empty thumb, no reflow as it lands.
   *
   * Calls back with `false` (show text-only) on error or once PRELOAD_TIMEOUT_MS is up.
   */
  function preloadThumb(src, done) {
    var settled = false;
    var timer = null;
    var finish = function (ok) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      done(ok);
    };

    timer = setTimeout(function () {
      finish(false);
    }, PRELOAD_TIMEOUT_MS);

    var img = new Image();
    img.onload = function () {
      // onload only means the bytes arrived; decode() waits until the bitmap is ready to
      // paint. Either resolution is good enough to reveal — a decode failure still leaves
      // us no worse off than showing on plain onload.
      if (typeof img.decode === 'function') {
        img.decode().then(
          function () {
            finish(true);
          },
          function () {
            finish(true);
          }
        );
        return;
      }
      finish(true);
    };
    img.onerror = function () {
      finish(false);
    };
    img.src = src;
  }

  function render(el, opts, src) {
    var productEl = el.querySelector('.vinyl-cart-toast__product');
    var suffixEl = el.querySelector('.vinyl-cart-toast__suffix');
    var thumbEl = el.querySelector('.vinyl-cart-toast__thumb');
    var imgEl = el.querySelector('.vinyl-cart-toast__img');

    if (productEl) {
      productEl.textContent = opts.title || '';
      // VNYL-1474: non-Alliance vendors ship separately, so the toast spells that out too.
      var separateShippingNote = opts.separateShipping
        ? '. Shipping calculated separately at checkout.'
        : '';
      // "<product> was added to your cart" reads wrong with no product name, so fall back to
      // carrying the whole message in the bold span and blanking the suffix.
      if (!opts.title && suffixEl) {
        productEl.textContent = 'Added to your cart';
        suffixEl.textContent = opts.separateShipping
          ? 'Shipping calculated separately at checkout'
          : '';
      } else if (suffixEl) {
        suffixEl.textContent = 'was added to your cart' + separateShippingNote;
      }
    }
    if (thumbEl && imgEl) {
      if (src) {
        imgEl.src = src;
        imgEl.alt = opts.title || '';
        thumbEl.hidden = false;
      } else {
        imgEl.removeAttribute('src');
        imgEl.alt = '';
        thumbEl.hidden = true;
      }
    }
  }

  function reveal(el) {
    // A second add while the first toast is still up replaces its content and restarts the
    // timer, rather than stacking a second toast or letting the first timer cut it short.
    clearTimeout(exitTimer);
    el.hidden = false;
    // Force a reflow so the enter transition also runs when re-showing a visible toast.
    void el.offsetWidth;
    el.classList.add('is-visible');
    // Started here rather than in show() so the 3.5s is 3.5s of *visible* toast, not time
    // partly spent waiting on the image.
    startDismissTimer();
  }

  function showToast(options) {
    var el = root();
    if (!el) return;
    bindOnce();

    var opts = options || {};
    var token = ++showToken;
    var src = thumbUrl(opts.imageUrl);

    if (!src) {
      render(el, opts, '');
      reveal(el);
      return;
    }

    preloadThumb(src, function (ok) {
      // A later add already claimed the toast — drop this one rather than clobbering it.
      if (token !== showToken) return;
      render(el, opts, ok ? src : '');
      reveal(el);
    });
  }

  function hideToast() {
    clearDismissTimer();
    // Cancel any in-flight preload too, so a toast that was dismissed (or pre-empted by
    // the cart drawer opening) can't still pop in once its image finishes loading.
    showToken++;
    var el = root();
    if (!el || el.hidden) return;
    el.classList.remove('is-visible');
    clearTimeout(exitTimer);
    exitTimer = setTimeout(function () {
      el.hidden = true;
    }, EXIT_MS);
  }

  function pulseCartIcon() {
    var toggle = document.getElementById(CART_TOGGLE_ID);
    if (!toggle) return;
    clearTimeout(pulseTimer);
    toggle.classList.remove('vinyl-cart-pulse');
    void toggle.offsetWidth;
    toggle.classList.add('vinyl-cart-pulse');
    pulseTimer = setTimeout(function () {
      toggle.classList.remove('vinyl-cart-pulse');
    }, PULSE_MS);
  }

  function writeLabel(button, text) {
    var label = button.querySelector('.buy-now');
    if (label) {
      label.textContent = text;
      return;
    }
    button.textContent = text;
  }

  function clearButtonTimer(button) {
    var timer = buttonTimers.get(button);
    if (timer) {
      clearTimeout(timer);
      buttonTimers.delete(button);
    }
  }

  /** True while an add fired from this button is still in flight. */
  function isBusy(button) {
    return !!button && button.dataset.vinylBuyBusy === '1';
  }

  /**
   * Drive an add-to-cart button through the states in the VNYL-1405 design. Used on both
   * paths — grid cards and PDP buttons alike:
   *   'adding' — disabled, "Adding…"
   *   'added'  — "Added ✓", auto-reverts after ADDED_LABEL_MS
   *   'reset'  — restore the original markup (also used on failure)
   *
   * The original innerHTML is stashed rather than rebuilt, so per-button variations come
   * back exactly as they were — "pre-order" labels, price spans, and the PDP's nested
   * `.spin` element.
   */
  function setButtonState(button, state) {
    if (!button) return;

    if (state === 'adding') {
      if (typeof button.dataset.vinylBuyLabel !== 'string') {
        button.dataset.vinylBuyLabel = button.innerHTML;
      }
      button.dataset.vinylBuyBusy = '1';
      button.classList.remove('button-buy--added');
      button.classList.add('button-buy--adding');
      button.setAttribute('aria-busy', 'true');
      writeLabel(button, 'Adding…');
      clearButtonTimer(button);
      buttonTimers.set(
        button,
        setTimeout(function () {
          setButtonState(button, 'reset');
        }, STUCK_RESET_MS)
      );
      return;
    }

    if (state === 'added') {
      delete button.dataset.vinylBuyBusy;
      button.removeAttribute('aria-busy');
      button.classList.remove('button-buy--adding');
      button.classList.add('button-buy--added');
      writeLabel(button, 'Added ✓');
      clearButtonTimer(button);
      buttonTimers.set(
        button,
        setTimeout(function () {
          setButtonState(button, 'reset');
        }, ADDED_LABEL_MS)
      );
      return;
    }

    clearButtonTimer(button);
    delete button.dataset.vinylBuyBusy;
    button.removeAttribute('aria-busy');
    button.classList.remove('button-buy--adding', 'button-buy--added');
    if (typeof button.dataset.vinylBuyLabel === 'string') {
      button.innerHTML = button.dataset.vinylBuyLabel;
      delete button.dataset.vinylBuyLabel;
    }
  }

  window.VinylAddToCart = {
    showToast: showToast,
    hideToast: hideToast,
    isBusy: isBusy,
    setButtonState: setButtonState,
    pulseCartIcon: pulseCartIcon,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindOnce);
  } else {
    bindOnce();
  }
})();
